import { eq } from "@-/com.npmjs.drizzle-orm";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { StaticDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.static";
import { staticSchema } from "@-/dev.improve.haniverse.database.schemas.static.schema";
import { getMnemonicGraphemeLabel } from "@-/dev.improve.haniverse.lib.morpheme.description";
import { createUseQueryOptions } from "@-/dev.improve.haniverse.lib.query-options";
import { getQueryDatabaseId } from "#get-query-database-id";

function useGetMorphemeMnemonicUsageCharactersQueryKey() {
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);
  const [staticDb] = useContextSelectors(StaticDatabaseContext, (c) => c.db);
  return (grapheme: string, hanziScript: "simplified" | "traditional") =>
    [
      "morphemeMnemonicUsageCharacters",
      grapheme,
      hanziScript,
      { contentDb, staticDb },
      getQueryDatabaseId(contentDb),
      getQueryDatabaseId(staticDb),
    ] as const;
}

/**
 * Characters whose mnemonic uses the grapheme: it is a component of their mnemonic decomposition
 * (`grapheme_component` is assembled from `decomposition.mnemonic`) AND their authored mnemonic
 * links to it. Ordered most frequent first; only mounted sheet rows hydrate their detail slice.
 */
export const [
  useMorphemeMnemonicUsageCharactersQueryOptions,
  morphemeMnemonicUsageCharactersQueryFn,
] = createUseQueryOptions(
  useGetMorphemeMnemonicUsageCharactersQueryKey,
  async ({ queryKey: [, grapheme, hanziScript, { contentDb, staticDb }] }) => {
    const db = staticDb(import.meta.location);
    // Keyed by glyph (not id) so a sheet can open the usages of any character it lists.
    const component = await db.query.grapheme.findFirst({
      columns: { id: true },
      where: { glyph: grapheme },
    });
    if (component === undefined) {
      return [];
    }
    const rows = await db
      .selectDistinct({
        biauRank: staticSchema.graphemeBiau.rank,
        character: staticSchema.grapheme.glyph,
        subtlexRank: staticSchema.graphemeSubtlex.rank,
      })
      .from(staticSchema.graphemeComponent)
      .innerJoin(
        staticSchema.grapheme,
        eq(staticSchema.grapheme.id, staticSchema.graphemeComponent.graphemeId),
      )
      .leftJoin(
        staticSchema.graphemeBiau,
        eq(staticSchema.graphemeBiau.graphemeId, staticSchema.grapheme.id),
      )
      .leftJoin(
        staticSchema.graphemeSubtlex,
        eq(staticSchema.graphemeSubtlex.graphemeId, staticSchema.grapheme.id),
      )
      .where(eq(staticSchema.graphemeComponent.componentGraphemeId, component.id));

    const ranked: { character: string; rank: number }[] = [];
    // Keep each remote D1 IN-list below its parameter limit.
    for (let offset = 0; offset < rows.length; offset += 64) {
      const page = rows.slice(offset, offset + 64);
      const authored = await contentDb(import.meta.location).query.appGraphemeMeaningCard.findMany({
        columns: { grapheme: true },
        where: { grapheme: { in: page.map((row) => row.character) } },
        with: { description: { columns: { mnemonic: true } } },
      });
      const charactersUsingMnemonic = new Set(
        authored
          .filter(
            (card) =>
              card.description !== null &&
              getMnemonicGraphemeLabel(card.description.mnemonic, grapheme) !== undefined,
          )
          .map((card) => card.grapheme),
      );
      for (const row of page) {
        if (charactersUsingMnemonic.has(row.character)) {
          const rank = hanziScript === "traditional" ? row.biauRank : row.subtlexRank;
          ranked.push({ character: row.character, rank: rank ?? Number.POSITIVE_INFINITY });
        }
      }
    }
    ranked.sort((a, b) => a.rank - b.rank || a.character.localeCompare(b.character));
    return ranked.map((row) => row.character);
  },
);
